import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import StudyController from "@/network/controllers/study.controller";
import QrController from "@/network/controllers/qr.controller"; // FE controller già esistente
import PatientSidebar from "../../components/dashboard/PatientSidebar";
import Button from "../../components/ui/Button";
import LogoSection from "../../components/ui/LogoSection";
import Modal from "../../components/ui/Modal";
import FancyQr from "@/components/qr/FancyQr";
import ParticipantController from "@/network/controllers/participant.controller";

export default function PatientQRCode({ onSignOut = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSession();

  // Backend data (consent/details)
  const [patientData, setPatientData] = useState(
    location.state?.patientData || null
  );
  const [loadingPatient, setLoadingPatient] = useState(
    !location.state?.patientData
  );
  const [patientError, setPatientError] = useState(null);

  // UI
  const [showSidebar, setShowSidebar] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  // QR
  const [qrUrl, setQrUrl] = useState(null); // data:image/png;base64,...
  const [qrCipher, setQrCipher] = useState(null); // encrypted string
  const [loadingQr, setLoadingQr] = useState(false);
  const [qrError, setQrError] = useState(null);

  // Fetch fallback if page opened directly
  useEffect(() => {
    if (patientData) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingPatient(true);
        setPatientError(null);
        const participantUserId = user?.userId;
        if (!participantUserId) {
          setPatientError({
            message: "Patient ID missing in session.",
            errorCode: "missing-patient-id",
            errorKey: 761204,
          });
          return;
        }
        const res = await StudyController.getPatientConsentDetails(
          participantUserId
        );
        if (!mounted) return;
        if (res?.success === false) {
          setPatientError({
            message: res.message || "Failed to fetch patient details.",
            errorCode: res.errorCode || "patient-details-fetch-failed",
            errorKey: 205944,
          });
        } else if (res) {
          setPatientData(res);
        } else {
          setPatientError({
            message: "No data received for patient details.",
            errorCode: "empty-patient-details",
            errorKey: 908441,
          });
        }
      } catch (err) {
        if (!mounted) return;
        setPatientError({
          message:
            err?.message || "Unexpected error while fetching patient details.",
          errorCode: "unexpected-patient-fetch",
          errorKey: 670352,
        });
      } finally {
        if (mounted) setLoadingPatient(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [patientData, user]);

  // Derived fields for display
  const derived = useMemo(() => {
    const name = `${patientData?.patient_firstname || ""} ${
      patientData?.patient_lastname || ""
    }`.trim();
    const investigatorName = `${patientData?.investigator_firstname || ""} ${
      patientData?.investigator_lastname || ""
    }`.trim();

    return {
      name,
      investigatorName,
      studyName: patientData?.study_name || "—",
      // site
      siteName:
        patientData?.site_name || patientData?.investigator_location || "",
      siteAddress: patientData?.site_address || "",
      // investigator contacts
      investigatorEmail: patientData?.investigator_email || "",
      investigatorPhone: patientData?.investigator_phone || "",
    };
  }, [patientData]);

  // Generate QR when we have participant_id + did
  useEffect(() => {
    const participantId = patientData?.participant_id;
    if (!participantId) return;
  
    let active = true;
    (async () => {
      try {
        setLoadingQr(true);
        setQrError(null);
        setQrUrl(null);
        setQrCipher(null);
      
  
        const res = await QrController.generateQrCode(participantId);
        if (!active) return;
  
        if (res?.success === false) {
          setQrError(res); // service already formatted
        } else {
          setQrCipher(res?.accessUrl || null); // 🔁 ora contiene l'URL leggibile dalla fotocamera
          setQrUrl(null); // QR image no longer used
        }
      } catch (err) {
        if (!active) return;
        setQrError({
          message: err?.message || "QR generation failed.",
          errorCode: "qr-generation-failed",
          errorKey: 821437,
        });
      } finally {
        if (active) setLoadingQr(false);
      }
    })();
  
    return () => {
      active = false;
    };
  }, [patientData]);

  
  

  const toggleSidebar = () => setShowSidebar(!showSidebar);

  const handlePrintQRCode = () => {
    if (!qrCipher || qrError) return;
    requestAnimationFrame(() => window.print());
  };

  // -----------------------------------------------
  // TODO: Call Blockchain to revoke patient consent
  // -----------------------------------------------
  const handleWithdrawConsent = () => setShowWithdrawModal(true);
  const confirmWithdrawal = async () => {
    console.log(" Triggering consent withdrawal...");
    if (!patientData?.study_id || !patientData?.consent_id) {
      console.error("Missing study_id or consent_id in patientData");
      return;
    }
  
    try {
      const result = await ParticipantController.revokePatientConsent({
        consent_id: patientData.consent_id,
        participant_id: patientData.participant_id,
        site_icf_id: patientData.site_icf_id,
        studyId: patientData.study_id,
      });
  
      if (result?.success === false) {
        console.error(" Consent withdrawal failed:", result);
        alert(`Consent withdrawal failed: ${result.message}`);
        return;
      }
  
      console.log(" Consent successfully withdrawn:", result);
      setShowWithdrawModal(false);
      navigate("/patient-qr-code-error"); 
    } catch (err) {
      console.error(" Unexpected error in consent withdrawal:", err);
      alert("Unexpected error occurred during withdrawal.");
    }
  };

  // Loading / error (patient data)
  if (loadingPatient) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Loading QR page…</p>
      </div>
    );
  }
  if (patientError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-semibold">
            Unable to load patient data
          </p>
          <p className="text-sm text-gray-600 mt-1">{patientError.message}</p>
          <p className="text-xs text-gray-400 mt-1">
            [{patientError.errorCode}] • {patientError.errorKey}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SCREEN LAYOUT */}
      <div className="flex h-screen relative print:hidden">
        {/* Mobile menu button */}
        <button
          onClick={toggleSidebar}
          className="md:hidden fixed top-4 left-4 z-50 bg-white p-2 rounded-md shadow-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Sidebar */}
        <div
          className={`${
            showSidebar ? "block" : "hidden"
          } md:block fixed md:relative z-40 h-full`}
        >
          <PatientSidebar
            patientName={derived.name || "Patient"}
            onSignOut={onSignOut}
            showSignature={false}
            showQRCode={true}
          />
        </div>

        {/* Overlay */}
        {showSidebar && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setShowSidebar(false)}
          />
        )}

        <div className="flex-1 overflow-auto bg-dashboard-bg">
          <div className="p-4 flex justify-between items-center">
            <div className="font-bold mt-2 text-xl ml-8 md:ml-0">
              {derived.studyName}
            </div>
          </div>

          <main className="p-4 md:p-6">
            <div className="bg-dashboard-bg1 p-4 md:p-6 rounded-xl flex-1 flex flex-col">
              <div className="flex flex-col md:flex-row">
                {/* Content */}
                <div className="w-full md:w-2/3 pr-0 md:pr-6">
                  <h2 className="text-xl font-semibold mb-4">
                    QR code for clinical study participation
                  </h2>

                  <div className="space-y-6">
                    <p>
                      Present this QR code at the study center
                      {derived.investigatorName
                        ? ` (${derived.investigatorName}${
                            derived.investigatorLocation
                              ? ` – ${derived.investigatorLocation}`
                              : ""
                          })`
                        : ""}
                    </p>

                    {/* QR area */}
                    <div className="flex justify-center min-h-[14rem]">
                      {loadingQr && (
                        <div className="w-56 h-56 grid place-content-center rounded-md bg-white/60 text-sm text-gray-500">
                          Generating QR…
                        </div>
                      )}

                      {!loadingQr && qrError && (
                        <div className="w-56 h-56 grid place-content-center rounded-md bg-red-50 text-red-600 text-center p-3">
                          <div className="font-semibold">
                            QR generation failed
                          </div>
                          <div className="text-xs mt-1">
                            {qrError.message || "Please retry later."}
                          </div>
                          <div className="text-[10px] mt-1 opacity-70">
                            [{qrError.errorCode}] • {qrError.errorKey}
                          </div>
                        </div>
                      )}

                      {!loadingQr &&
                        !qrError &&
                        (qrCipher ? (
                          <FancyQr
                            data={qrCipher}
                            size={320}
                            logo="/img/logo.png"
                            preset="clinical"
                            ecl="M"
                            logoScale={0.3}
                          />
                        ) : (
                          qrUrl && (
                            <img
                              src={qrUrl}
                              alt="Participant QR Code"
                              className="w-56 h-56 p-2 rounded-md bg-white shadow"
                            />
                          )
                        ))}
                    </div>

                    <div>
                      <p className="mb-8">
                        This QR code serves as confirmation of your
                        participation in the clinical study.
                      </p>
                      <p>
                        Please present this QR code during your visits to the
                        study center for verification. It will be used for
                        identification and further tracking throughout the study
                        process.
                      </p>
                    </div>

                    <div className="pl-2 md:pl-6 space-y-6 md:space-y-4 text-sm">
                      <div className="flex justify-center">
                        <Button
                          onClick={handlePrintQRCode}
                          disabled={!qrCipher || !!qrError} 
                          variant="QrCode"
                          className="sm:ml-2 sm:w-auto text-white min-w-[300px] text-lg font-semibold rounded-xl disabled:opacity-60"
                        >
                          Print QR Code
                        </Button>
                      </div>

                      <div className="border-t pt-4">
                        <p>
                          You can withdraw your consent at any time during the
                          study. If you wish to do so, please follow this link:{" "}
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowWithdrawModal(true);
                            }}
                            className="underline cursor-pointer"
                          >
                            Withdraw my consent
                          </a>
                        </p>
                      </div>

                      <div className="h-10" />
                      <div className="h-10" />
                      <div className="h-5" />
                    </div>
                  </div>
                </div>

                {/* Logo section */}
                <LogoSection />
              </div>
            </div>
          </main>
        </div>

        {/* Withdraw Consent Modal */}
        <Modal
          isOpen={showWithdrawModal}
          onClose={() => setShowWithdrawModal(false)}
          title="Confirm consent withdrawal"
          primaryAction={confirmWithdrawal}
          primaryActionLabel="Withdrawal"
          primaryButtonVariant="closestudy"
          secondaryAction={() => setShowWithdrawModal(false)}
          secondaryActionLabel="Cancel"
          secondaryButtonVariant="addbutton"
          size="xl"
        >
          <div className="space-y-4">
            <p>
              Are you sure you want to withdraw your consent from the clinical
              study?
            </p>
            <p>
              Withdrawing your consent means that you will no longer be able to
              participate in the study.
            </p>
            <p>
              Please note that after withdrawal, you will no longer be involved
              in any further study procedures.
            </p>
          </div>
        </Modal>
      </div>

      {/* --- PRINT-ONLY  --- */}
      <div className="hidden print:block bg-white text-black">
        <div className="mx-auto my-6 w-[420px]">
          <div className="border rounded-xl p-5 shadow-none">
            <h1 className="text-center font-semibold mb-4">
              [{derived.studyName || "Study name"}]
            </h1>

            <div className="flex justify-center mb-4">
              {qrCipher ? (
                <FancyQr
                  data={qrCipher}
                  size={320}
                  logo="/img/logo.png"
                  preset="clinical"
                  ecl="M"
                  logoScale={0.3}
                />
              ) : (
                <div className="w-28 h-28 border" />
              )}
            </div>

            <p className="text-sm mb-3">
              Please present this QR code during your visits to the study
              center.
            </p>
            <p className="text-sm mb-3">
              This QR code serves as confirmation of your participation in the
              clinical study. It will be used for identification and further
              tracking throughout the study process.
            </p>

            <div className="text-sm mt-4 space-y-1">
              <p className="font-semibold">Study center address :</p>
              <p>{derived.siteName || "—"}</p>
              <p>{derived.siteAddress || "—"}</p>

              <p className="font-semibold mt-3">Contact informations :</p>
              <p>{derived.investigatorName || "—"}</p>
              <p>{derived.investigatorPhone || "—"}</p>
              <p>{derived.investigatorEmail || "—"}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}